import { useEffect, useState, useMemo } from 'react';
import _ from 'lodash';
import { useDispatch, useSelector } from "react-redux";
import {notification} from "antd";
import {
    addEtfToSmaConfig,
    removeEtfFromSmaConfig,
    updateSmaConfigInstance,
    initializeNewSmaConfig,
    ensureDefaultSmaConfig,
    initializeSmaWithTemplate
} from "../../store/slices/sma.slice";
import { fetchTickerData } from "../../api/tickersApi";
import { selectAllTickerAttrs } from "../../store/slices/tickers.slice";
import {
    mergeEtfs,
    cutMergedHoldings,
    getSectorColors,
    getSectorsBarData,
    applyBlacklist,
    applyScreen,
} from "./utils/smaUtils";
import {selectAppDataByType} from "../../store/slices/user.slice";
import {
    equalFillPortfolio,
    proRataFillPortfolio,
    scalePortfolioToTarget,
    sectorTargetFillPortfolio,
    normalizeWeights
} from "../../lib/weightSchemes";
import {createNewPortfolio} from "../../api/portfolioApi";
import {SCREENER_FIELDS} from "../../constants";
import {getSanitizedTicker} from "../../utils/abbreviations";


const useSma = () => {
    const dispatch = useDispatch();
    const currentSmaConfigId = useSelector(state => state.sma.currentSmaConfigId);

    useEffect(() => {
        if (!currentSmaConfigId) {
            dispatch(ensureDefaultSmaConfig());
        }
    }, [dispatch, currentSmaConfigId]);


    // Access the users saved blacklists and screeners if they exist
    const blacklists = useSelector(state => selectAppDataByType(state, 'blacklist'));
    const screens = useSelector(state => selectAppDataByType(state, 'screener'));

    // Access the current SMA config and current SMA config ID
    const smaConfigs = useSelector(state => state.sma.smaConfigs);
    const currentSmaConfig = smaConfigs[currentSmaConfigId];

    const [sectorsBarChartField, setSectorsBarChartField] = useState('wgt');
    const [readySmaConfigId, setReadySmaConfigId] = useState(currentSmaConfigId);  // To handle async creation of SMA
    const [filteredHoldings, setFilteredHoldings] = useState(null);
    const [replicateKey, setReplicateKey] = useState(0);


    const etfData = useSelector((state) => selectAllTickerAttrs(
        state, Object.keys(currentSmaConfig?.etfs || {}), SCREENER_FIELDS));


    // Function to ensure a valid SMA config exists before adding ETFs
    const ensureSmaConfig = () => {
        if (readySmaConfigId || currentSmaConfigId) {
            return readySmaConfigId || currentSmaConfigId;
        }

        const newSmaConfigId = currentSmaConfigId || dispatch(initializeNewSmaConfig()).payload;
        setReadySmaConfigId(newSmaConfigId);
        return newSmaConfigId;
    };

    useEffect(() => {
        if (currentSmaConfigId) {
            setReadySmaConfigId(currentSmaConfigId);
        }
    }, [currentSmaConfigId]);

    // Function to add ETF to the current SMA configuration
    const addEtf = (etfId, etfWeight = 0) => {
        ensureSmaConfig();  // Ensure a config exists before adding ETFs

        // Delay execution until SMA config is ready
        const smaConfigId = readySmaConfigId || currentSmaConfigId;

        if (!smaConfigId) return;

        // Dispatch the action to add the ETF to the SMA config
        dispatch(addEtfToSmaConfig({ smaConfigId, etfId, etfWeight }));

        // Fetch the ticker data for the ETF
        dispatch(fetchTickerData({
            symbols: [etfId],
            fields: SCREENER_FIELDS,
            collection: 'US'
        }));
    };

      const addEtfs = (etfIds) => {
            const smaConfigId = ensureSmaConfig();
            etfIds.forEach(etfId => {
                addEtf(etfId, 0);
            });
      };

      const initializeWithTemplate = (etfs, linkedPortfolioId) => {
        const smaConfigId = _.uniqueId('sma_');  // Generate a new config ID
        // Dispatch the new action to initialize the SMA with ETFs from the template
        dispatch(initializeSmaWithTemplate({ smaConfigId, etfs, linkedPortfolioId }));
        addEtfs(etfs.map(etf => etf.symbol.toLowerCase()));
    };



    // Function to remove ETF from the current SMA configuration
    const removeEtf = (etfId) => {
        if (!readySmaConfigId) return;
        // Dispatch the action to remove the ETF from the SMA config
        dispatch(removeEtfFromSmaConfig({ smaConfigId: readySmaConfigId, etfId }));
    };

    /**
     * Function to update the weight of an ETF in the current SMA configuration
     * @param etfId - Ticker symbol of the ETF
     * @param newWeight - New weight of the ETF as a percentage (0-100)
     */
    const updateEtfWeight = (etfId, newWeight) => {
        if (!readySmaConfigId || !currentSmaConfig.etfs[etfId]) return;
        // Dispatch the action to update the ETF weight
        const updatedConfig = { etfs: { [etfId]: {weight: newWeight * 0.01 || 0} }};
        dispatch(updateSmaConfigInstance({ smaConfigId: readySmaConfigId, updatedConfig }));
        setReplicateKey(replicateKey + 1)
    };

    /**
     * Function to toggle the replicability of an ETF in the current SMA configuration.
     * If doNotReplicate is true, the ETF will not be included in the SMA calculations
     *
     * @param etfId {string}
     * @param doNotReplicate {boolean} - True if the ETF should not be included in the SMA calculations
     */
    const toggleReplicateEtf = (etfId, doNotReplicate) => {
        if (!readySmaConfigId || !currentSmaConfig.etfs[etfId]) return;

        // Check if the current change will result in all ETFs being non-replicable
        const otherEtfs = Object.keys(currentSmaConfig.etfs).filter(e => e !== etfId);
        const nonReplicableEtfs = otherEtfs.filter(e => currentSmaConfig.etfs[e].doNotReplicate);

        if (nonReplicableEtfs.length === otherEtfs.length && doNotReplicate) {
            notification.error({ message: "At least one ETF must be replicable." });
            return;
        }

        const updatedConfig = { etfs: { [etfId]: { doNotReplicate } }};
        dispatch(updateSmaConfigInstance({ smaConfigId: readySmaConfigId, updatedConfig }));
        setReplicateKey(replicateKey + 1);
    };

    /**
     * Function to update the current SMA configuration (currentSmaConfigId) with a new value
     * @param field
     * @param value
     */
    const updateConfig = (field, value) => {
        if (readySmaConfigId) {
            dispatch(updateSmaConfigInstance({
                smaConfigId: readySmaConfigId,
                updatedConfig: { [field]: value }
            }));
        }
    };



    // Step 1: Merge ETFs into consolidated stock list
    const mergedData = useMemo(() => {
        if (etfData && Object.keys(etfData).length > 0) {
            return mergeEtfs(Object.keys(etfData).filter(etf=>!currentSmaConfig.etfs[etf].doNotReplicate===true).map(etf => ({
                symbol: etf,
                weight: currentSmaConfig.etfs[etf].weight || 0,
                holdings: Array.isArray(etfData[etf]?.holdings) ? Object.values(etfData[etf]?.holdings) : []
            })));
        }
        return null;
    }, [etfData, currentSmaConfig?.etfs, replicateKey, currentSmaConfigId, currentSmaConfig?.fillType]);

    const filterHoldings = async (mergedHoldings) => {
        let filteredHoldings = mergedHoldings;
        const selectedBlacklist = currentSmaConfig.blacklistId && blacklists.find(bl => bl.id === currentSmaConfig.blacklistId);
        const selectedScreener = currentSmaConfig.screenerId && screens.find(s => s.id === currentSmaConfig.screenerId);

        if (selectedBlacklist) {
            const { filteredTickers, removed } = applyBlacklist(filteredHoldings, selectedBlacklist.data);
            filteredHoldings = filteredTickers;
        }

        if (selectedScreener) {
            const { screenedTickers } = await applyScreen(dispatch, selectedScreener.data, filteredHoldings);
            filteredHoldings = screenedTickers;
        }


        return filteredHoldings;
    };

    // Merge ETFs and filter holdings asynchronously
    useEffect(() => {
        const applyFilters = async () => {
            if (mergedData) {
                const filtered = await filterHoldings(mergedData);
                setFilteredHoldings(filtered);
            }
        };
        applyFilters();
    }, [currentSmaConfig?.blacklistId, currentSmaConfig?.screenerId,
        replicateKey, currentSmaConfigId, JSON.stringify(etfData)]);

    /**
     * Step 2.a) Cut the merged holdings based on the cutoff weight
     */
    const cutoffWeight = useMemo(()=>{
        return currentSmaConfig?.cutoffWeight || 80;
    }, [currentSmaConfig?.cutoffWeight])

    const holdingsAfterCutoff = useMemo(() => {
        const holdings = filteredHoldings ? filteredHoldings : mergedData;
        return holdings ? cutMergedHoldings(holdings, cutoffWeight / 100) : null;
    }, [mergedData, cutoffWeight, replicateKey, currentSmaConfigId, currentSmaConfig?.etfs]);


    /**
     * Step 3: Fill the holdings based on the 'fillType' of current SMA
     * fillTypes = ['equal', 'prorata', 'sectors']
     *
     */
    const fillType = currentSmaConfig?.fillType || 'prorata';  // Default to 'proRata'
    const {
        filledHoldings = [],
        sectorRemainders = null,
        sectorWeightChanges=null
    } = useMemo(() => {
        if (!holdingsAfterCutoff) return { filledHoldings: [], sectorRemainders: null }; // Ensure it returns an object with default values.

        let portfolioHoldings = [];
        let sectorRemainders = null;
        let sectorWeightChanges = null;

        try {
            switch (fillType) {
                case 'equal':
                    portfolioHoldings = equalFillPortfolio(holdingsAfterCutoff);
                    break;

                case 'prorata':
                    portfolioHoldings = proRataFillPortfolio(holdingsAfterCutoff, currentSmaConfig?.maxPerHolding || 0);
                    break;

                case 'sectors':
                    const tickersExcluded = mergedData.filter(h => !holdingsAfterCutoff.find(h2 => h2.symbol === h.symbol));
                    if (!tickersExcluded || tickersExcluded.length === 0) {
                        notification.error({
                            message: "No other holdings to include in the sector refill",
                            description: "A Pro Rata weighting was applied instead."
                        });
                        throw new Error("Cannot use 'sectors' fill type when there are no other holdings to include in the refill.");
                    }
                    let sectorFillResults = sectorTargetFillPortfolio(
                        holdingsAfterCutoff,
                        tickersExcluded,
                        currentSmaConfig?.minSectorTolerance,
                        currentSmaConfig?.maxPerHolding || 0
                    );
                    portfolioHoldings = sectorFillResults[0];
                    sectorRemainders = sectorFillResults[1];
                    sectorWeightChanges = sectorFillResults[2];
                    break;

                default:
                    throw new Error(`Unknown fill type: ${fillType}`);
            }
        } catch (e) {
            notification.error({ message: "Error or Unknown fillType", description: e.message });
        }

        const nonReplicableEtfs = Object.keys(currentSmaConfig.etfs)
            .filter(etfId => currentSmaConfig.etfs[etfId].doNotReplicate)
            .map(etfId => ({
                symbol: etfId,
                wgt: currentSmaConfig.etfs[etfId].weight,
                name: etfData[etfId]?.name ?? etfId,
                type: 'etf'
            }));

        const nonReplicableWeightSum = nonReplicableEtfs.reduce((sum, etf) => sum + etf.wgt, 0);
        const stocksScaled = nonReplicableWeightSum > 0 ? scalePortfolioToTarget(portfolioHoldings, 1 - nonReplicableWeightSum) : portfolioHoldings;

        return {
            filledHoldings: stocksScaled ? [...stocksScaled, ...nonReplicableEtfs] : [],
            sectorRemainders,
            sectorWeightChanges
        };
    }, [holdingsAfterCutoff, fillType, currentSmaConfig?.maxPerHolding, etfData, replicateKey]);

    const originalSectorDistribution = useMemo(() => {
        if (!mergedData) return null;
        return mergedData.reduce((acc, holding) => {
            if (holding.sector) {
                acc[holding.sector] = (acc[holding.sector] || 0) + holding.wgt;
            }
            return acc;
        }, {});
    }, [mergedData]);

    // Step 4: Calculate summary details of cut vs full holdings
    const summaryDetails = useMemo(() => {
        if (!filledHoldings) return null;

        const totalWeight = filledHoldings.reduce((sum, holding) => sum + holding.wgt, 0);
        const sectors = filledHoldings.reduce((acc, holding) => {
            if (holding.sector) {
                acc[holding.sector] = (acc[holding.sector] || 0) + holding.wgt;
            }
            return acc;
        }, {});

        return {
            totalHoldings: filledHoldings?.length,
            originalHoldings: mergedData?.length,
            totalWeight,
            sectorDistribution: sectors,
            originalSectorDistribution,
        };
    }, [filledHoldings]);

   /* Maps etfs to their respective colors */
   const etfColors = currentSmaConfig?.etfs
        ? Object.keys(currentSmaConfig.etfs).reduce((acc, etfId) => {
            acc[etfId] = currentSmaConfig.etfs[etfId].color;
            return acc;
        }, {})
        : {};

   /* Assigns sector colors for each unique sector */
   const sectorColors = useMemo(()=>{
       return mergedData ? getSectorColors(mergedData) : {};
   });

    /* Prepares the sectors bar data for cut holdings vs full holdings sector comparison */
   const sectorsBarData = filledHoldings && mergedData && getSectorsBarData(normalizeWeights([...filledHoldings].filter(h => h.type !== 'etf')), mergedData, sectorsBarChartField)


   const allSectors = useMemo(() => {
         if (!mergedData) return null;
         return [...new Set(mergedData.map(h => h.sector))];
   })


   const getSectorTickers = (sector, nTickers)=>{
         if (!mergedData) return null;
         let sectorTickers = [...mergedData].filter(h => h.sector === sector);
         if (nTickers) {
              sectorTickers = sectorTickers.slice(0, nTickers);
         }
         return normalizeWeights([...sectorTickers]);
   }

   const saveSma = async () => {
       const isNew = currentSmaConfigId.startsWith('sma_');
        if (!filledHoldings) return;

        const smaProps = currentSmaConfig;
        const holdings = filledHoldings.map(item => ({
            id: getSanitizedTicker(item.symbol),
            wgt: item.wgt,
            name: item?.name ?? '',  // Map stock names if available
            type: 'stock'
        }));

        // Save using the createNewPortfolio API
        dispatch(createNewPortfolio({
            title: smaProps.name || "Unnamed SMA",
            itemArray: holdings,
            type: 'b',
            props: {
                sma_config: smaProps
            }
        }));
    };


    return {
        initializeWithTemplate,
        currentSmaConfig, updateConfig,
        etfData, mergedData, filledHoldings, summaryDetails,
        addEtf, addEtfs, removeEtf, updateEtfWeight, toggleReplicateEtf,
        etfColors, sectorColors,
        blacklists, screens,
        sectorsBarData, sectorsBarChartField, setSectorsBarChartField,
        saveSma,
        replicateKey,
        getSectorTickers, allSectors,
        sectorRemainders,
        sectorWeightChanges

    };
};

export default useSma;
